import React from 'react';
import { Bubble, Button, Form, Input } from 'tea-component';
import { connect } from 'dva';
import { LoadingOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { allowChartTypeShowUserFilter } from '@dbl/core/global';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import { SortableType } from '@dbl/core/chartEditor';
import { FilterSortable } from '@/pages/datalab/components/SortableDrag/Sortable/filterSortable';
import { MetricSortable } from '@/pages/datalab/components/SortableDrag/Sortable/metricSortable';
import { DimensionSortable } from '@/pages/datalab/components/SortableDrag/Sortable/dimensionSortable';
import { ShowDimensionSortable } from '@/pages/datalab/components/SortableDrag/Sortable/showDimensionSortable';
import { RawColumnSortable } from '@/pages/datalab/components/SortableDrag/Sortable/rowColumnSortable';
import UserFilter from '@/pages/datalab/components/UserFilter';
import { PreviewSQLModal } from '@dbl/domainComponents/dataSource';
import { selectSearchConnectedProps } from './selectors/mapSearchViewModel';
import { useSearchActions } from './hooks/useSearchActions';
import type { ISearchProps } from './types';
import './index.scss';

export default connect(selectSearchConnectedProps)(Search);

/** 多维分析顶部筛选表单（字段拖拽区 + 计算/清空/SQL 预览） */
function Search(props: ISearchProps) {
  const {
    dataTables,
    loading,
    columnUserFilters,
    customDimensionMap,
    mode,
    analysisRunning,
    chartType,
    rowLimit,
    save_column_infos,
    customColumns,
    dataTableJoinRelations,
    system,
    gameCode,
    hasUserFilters,
  } = props;

  const {
    multiFormatMessage,
    commonFormatMessage,
    formatMessage,
    isEn,
    dispatchFun,
    visibleSearch,
    toggleVisibleSearch,
    sqlLoading,
    isRowLimitBlur,
    sortableOption,
    setSortableOption,
    appendFilters,
    appendMetrics,
    appendDimensions,
    appendX,
    appendShowDimensions,
    appendRawColumns,
    showCustomColumnModel,
    handleSubmit,
    clearAll,
    previewSQL,
    previewSqlModalVisible,
    hidePreviewSqlModal,
    previewSqlContent,
    previewSqlError,
    cancelAnalysis,
    handleBlur,
    handleRowLimitChange,
    handleRowLimitPressEnter,
    handleUserFilter,
    isRawColumnMode,
    hiddenDimension,
    isCheckSuccess,
    isCleared,
    bubbleContent,
    hasDimensionMap,
    disableButton,
  } = useSearchActions({ props });

  if (loading) {
    return <>loading</>;
  }

  return (
    <div className="form-card-fd">
      <div
        className="form-card dtb-dl-ans-search-container"
        style={{ display: visibleSearch ? 'block' : 'none' }}
      >
        <form onSubmit={handleSubmit} className="search-form">
          <Form layout="inline" hideLabel={true}>
            <div
              id="multi-analysis-search-filtration"
              style={{ display: 'flex', flexDirection: 'column' }}
              className={`${mode === ChartCalculateMode.RawColumn && chartType === ChartType.Table ? 'search-form-raw' : chartType === ChartType.DimNumberTrend && isEn ? 'search-form-dim-number-trend' : ''}`}
            >
              <Form.Item className="them-blue">
                <div className="select-content">
                  <div className="form-title">
                    <h3>
                      <span
                        className={`${isRawColumnMode ? '' : 'dbl-form-title-required '}`}
                      >
                        {multiFormatMessage('filter_conditions')}
                      </span>
                    </h3>
                  </div>
                  <FilterSortable
                    key="filter"
                    sortableKey={SortableType.Filter}
                    sortableList={appendFilters}
                    sortableOption={sortableOption}
                    setSortableOption={setSortableOption}
                    dispatchFun={dispatchFun}
                    dataTables={dataTables}
                    dataTableJoinRelations={dataTableJoinRelations}
                    chartType={chartType}
                    mode={mode}
                    dimensions={appendDimensions}
                    showCustomColumnModel={showCustomColumnModel}
                    system={system}
                    customColumns={customColumns}
                  />
                </div>
              </Form.Item>
              {allowChartTypeShowUserFilter({ chartType, mode }) &&
                hasUserFilters && (
                <UserFilter
                  value={columnUserFilters as never}
                  gameCode={gameCode}
                  label={formatMessage({ id: 'dbl_filter_user_filters_label' })}
                  onChange={handleUserFilter}
                />
              )}
              {!isRawColumnMode && (
                <Form.Item className="them-green">
                  <div className="select-content">
                    <div className="form-title">
                      <h3 className="dbl-form-title-required">
                        {multiFormatMessage('statistical_indicators')}
                      </h3>
                    </div>
                    <MetricSortable
                      key="metric"
                      sortableKey={SortableType.Metric}
                      sortableList={appendMetrics}
                      sortableOption={sortableOption}
                      dispatchFun={dispatchFun}
                      isEn={isEn}
                      showCustomColumnModel={showCustomColumnModel}
                      dataTables={dataTables}
                      dataTableJoinRelations={dataTableJoinRelations}
                      chartType={chartType}
                      saveColumnInfos={save_column_infos}
                      customColumns={customColumns}
                    />
                  </div>
                </Form.Item>
              )}
              {chartType === ChartType.DimNumberTrend && (
                <Form.Item className="them-orange">
                  <div className="select-content">
                    <div className="form-title">
                      <h3>
                        <span className="dbl-form-title-required">
                          {multiFormatMessage('show_dimensions')}
                        </span>
                      </h3>
                    </div>
                    <ShowDimensionSortable
                      key="show_dimensions"
                      sortableKey={SortableType.ShowDimension}
                      sortableList={appendShowDimensions}
                      sortableOption={sortableOption}
                      dispatchFun={dispatchFun}
                      dimensionMap={customDimensionMap}
                      isEn={isEn}
                      showCustomColumnModel={showCustomColumnModel}
                      dataTables={dataTables}
                      dataTableJoinRelations={dataTableJoinRelations}
                      chartType={chartType}
                      filters={appendFilters}
                      customColumns={customColumns}
                    />
                  </div>
                </Form.Item>
              )}
              {!hiddenDimension && !isRawColumnMode && (
                <Form.Item className="them-orange">
                  <div className="select-content">
                    <div className="form-title">
                      <h3>
                        <span
                          className={classNames({
                            'dbl-form-title-required': ![
                              ChartType.Table,
                              ChartType.Pie,
                            ].includes(chartType as TChartType),
                          })}
                        >
                          {multiFormatMessage('categorical_dimensions')}
                        </span>
                      </h3>
                    </div>
                    <DimensionSortable
                      key="dimension"
                      sortableKey={SortableType.Dimension}
                      sortableList={appendDimensions}
                      sortableOption={sortableOption}
                      dispatchFun={dispatchFun}
                      isEn={isEn}
                      showCustomColumnModel={showCustomColumnModel}
                      dataTables={dataTables}
                      dataTableJoinRelations={dataTableJoinRelations}
                      chartType={chartType}
                      filters={appendFilters}
                      saveColumnInfos={save_column_infos}
                      system={system}
                      customColumns={customColumns}
                    />
                  </div>
                </Form.Item>
              )}
              {(chartType === ChartType.Trend ||
                chartType === ChartType.DimNumberTrend) && (
                <Form.Item className="them-orange">
                  <div className="select-content">
                    <div className="form-title">
                      <h3>
                        <span className="dbl-form-title-required">
                          {multiFormatMessage('x_axis')}
                        </span>
                      </h3>
                    </div>
                    <DimensionSortable
                      key="x"
                      sortableKey={SortableType.XColumn}
                      sortableList={appendX}
                      sortableOption={sortableOption}
                      dispatchFun={dispatchFun}
                      isEn={isEn}
                      showCustomColumnModel={showCustomColumnModel}
                      dataTables={dataTables}
                      dataTableJoinRelations={dataTableJoinRelations}
                      chartType={chartType}
                      filters={appendFilters}
                      saveColumnInfos={save_column_infos}
                      system={system}
                      customColumns={customColumns}
                    />
                  </div>
                </Form.Item>
              )}
              {isRawColumnMode && (
                <Form.Item className="them-green">
                  <div className="select-content">
                    <div className="form-title">
                      <h3 className="dbl-form-title-required">
                        {multiFormatMessage('raw_column')}
                      </h3>
                    </div>
                    <RawColumnSortable
                      key="rowColumn"
                      sortableKey={SortableType.RawColumn}
                      sortableList={appendRawColumns}
                      sortableOption={sortableOption}
                      dispatchFun={dispatchFun}
                      isEn={isEn}
                      showCustomColumnModel={showCustomColumnModel}
                      dataTables={dataTables}
                      dataTableJoinRelations={dataTableJoinRelations}
                      chartType={chartType}
                      system={system}
                      saveColumnInfos={save_column_infos}
                      customColumns={customColumns}
                    />
                  </div>
                </Form.Item>
              )}
            </div>
            <Form.Action className="form-action">
              {analysisRunning ? (
                <Button type="primary" onClick={cancelAnalysis}>
                  {multiFormatMessage('finish_calculate')}
                  <LoadingOutlined
                    style={{ color: 'white', marginLeft: '5px' }}
                  />
                </Button>
              ) : (
                <Bubble
                  style={{ maxWidth: 402 }}
                  dark
                  arrowPointAtCenter
                  placement="top"
                  content={
                    isCheckSuccess ? (
                      ''
                    ) : (
                      <div
                        style={{ lineHeight: '20px' }}
                        dangerouslySetInnerHTML={{ __html: bubbleContent }}
                      />
                    )
                  }
                >
                  <Button
                    type="primary"
                    disabled={
                      !isCheckSuccess ||
                      !hasDimensionMap ||
                      disableButton ||
                      isCleared
                    }
                    htmlType="submit"
                  >
                    {multiFormatMessage('calculate')}
                  </Button>
                </Bubble>
              )}
              <Button
                htmlType="button"
                disabled={disableButton || isCleared}
                onClick={clearAll}
              >
                {commonFormatMessage('clear')}
              </Button>
              <Button
                htmlType="button"
                disabled={
                  !isCheckSuccess || disableButton || sqlLoading || isCleared
                }
                onClick={previewSQL}
              >
                {multiFormatMessage('preview_sql')}
                {sqlLoading ? (
                  <LoadingOutlined style={{ marginLeft: '5px' }} />
                ) : (
                  ''
                )}
              </Button>
              <span className="limit-info">
                <span>{formatMessage({ id: 'dbl_chart_row_limit_info' })}</span>
                <Bubble
                  error
                  visible={isRowLimitBlur}
                  placement="top"
                  content={
                    <div className="dbi-download-tip">
                      {formatMessage({ id: 'dbl_chart_row_max_limit_info' })}
                    </div>
                  }
                >
                  <Input
                    value={String(+rowLimit)}
                    type="number"
                    size="s"
                    style={{
                      width: '85px',
                      borderColor: isRowLimitBlur
                        ? '#ef5858'
                        : +rowLimit
                          ? ' '
                          : '#326ff0',
                    }}
                    min={2000}
                    max={50000}
                    step={1}
                    onBlur={handleBlur}
                    onPressEnter={handleRowLimitPressEnter}
                    onChange={handleRowLimitChange}
                  />
                </Bubble>
              </span>
            </Form.Action>
          </Form>
        </form>
      </div>
      <div onClick={toggleVisibleSearch}>
        {visibleSearch ? (
          <div className="expand-collapse-top">
            <div className="expand-collapse-init" />
          </div>
        ) : (
          <div className="expand-collapse-bottom">
            <div className="expand-collapse-init" />
          </div>
        )}
      </div>
      <PreviewSQLModal
        visible={previewSqlModalVisible}
        onClose={hidePreviewSqlModal}
        sql={previewSqlContent}
        loading={sqlLoading}
        error={previewSqlError}
      />
    </div>
  );
}
